import React, { useState, useEffect, useMemo, ReactNode } from 'react';
import { Grid, Box, IconButton, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { FlexyTextField, useL2DTheme } from '@europrocurement/flexy-components';
import { useDebouncedCallback } from 'use-debounce';
import { PreFilterDatatable, SearchOpts, PreFilter } from '../DatatableHeader';
import { FlexyFiltersList } from '../FlexyFiltersList';
import { FlexyPreFilters } from '../FlexyPreFilters';

export type FlexyFiltersProps = {
    searchOpts?: SearchOpts;
    buttonAction?: ReactNode;
    onRefresh?: () => void;
    debounce?: number;
};

const isPreFilterActive = (
    preFilterItem: PreFilterDatatable,
    activeCriterion: Record<string, unknown>,
    mustHaveAFilter: boolean,
): boolean => {
    if (mustHaveAFilter && preFilterItem.filters.length === 0) {
        return false;
    }
    const totalCount = preFilterItem.filters.reduce((currentCount: number, f: PreFilter) => {
        if (activeCriterion[f.field] === f.value) {
            return currentCount + 1;
        }

        if (Array.isArray(f.value) && Array.isArray(activeCriterion[f.field])) {
            const tmp = (activeCriterion[f.field] as unknown[]).filter((o) => f.value === o);

            if (tmp.length === 0) {
                return currentCount + 1;
            }
        }

        return currentCount;
    }, 0);

    return totalCount === preFilterItem.filters.length;
};

export const FlexyFilters: React.FunctionComponent<FlexyFiltersProps> = function ({
    searchOpts,
    buttonAction,
    onRefresh,
    debounce = 500,
}: FlexyFiltersProps) {
    const { palette } = useL2DTheme();
    const [currentPreFilter, setCurrentPreFilter] = useState<PreFilterDatatable>();
    const preFilters = useMemo(
        () => searchOpts?.filtersOpts?.preFilters ?? { items: [] },
        [searchOpts?.filtersOpts?.preFilters],
    );
    const activePreFilters = searchOpts?.filtersOpts?.activePreFilter;

    const [innerValue, setInnerValue] = React.useState<string>(searchOpts?.search || '');

    useEffect(() => {
        let active = preFilters.items.find((item) =>
            isPreFilterActive(item, activePreFilters || {}, true),
        );

        if (!active) {
            active = preFilters.items.find((item) =>
                isPreFilterActive(item, activePreFilters || {}, false),
            );
        }

        if (active) {
            setCurrentPreFilter(active);
        }
    }, [preFilters, activePreFilters]);

    const applyPreFilter = (preFilter: PreFilterDatatable) => {
        setCurrentPreFilter(preFilter);
        searchOpts?.filtersOpts?.onUpdateFilters(preFilter);
    };

    const onChangeDebounce = useDebouncedCallback((value: string) => {
        if (searchOpts?.onSearchChange) {
            searchOpts?.onSearchChange(value);
        }
    }, debounce);

    return (
        <Grid
            container
            direction="column"
            alignItems="end"
        >
            <Grid
                item
                sx={{ width: '100%' }}
            >
                <Box
                    display="flex"
                    flexDirection="row-reverse"
                    justifyContent="space-between"
                    alignItems="end"
                    gap={2}
                >
                    <Box>{buttonAction}</Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'start',
                            justifyContent: 'start',
                            width: '100%',
                        }}
                    >
                        {preFilters.items.length > 0 && (
                            <FlexyPreFilters
                                filters={preFilters}
                                onClick={applyPreFilter}
                                current={currentPreFilter}
                            />
                        )}
                        {searchOpts?.showSearch ? (
                            <FlexyTextField
                                sx={{
                                    marginTop: '15px',
                                }}
                                inputProps={{
                                    style: {
                                        padding: '14px 14px',
                                    },
                                }}
                                value={innerValue}
                                onChange={(e) => {
                                    setInnerValue(e.target.value);
                                    onChangeDebounce(e.target.value);
                                }}
                                variant="outlined"
                                fullWidth
                                placeholder="Recherche textuelle"
                            />
                        ) : null}
                    </Box>
                </Box>
            </Grid>
            <Grid
                item
                alignSelf="start"
                sx={{ marginTop: '15px', width: '100%' }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <Box
                        sx={{
                            flex: '1 1 auto',
                            minWidth: 0,
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            '&::-webkit-scrollbar': {
                                height: '6px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: 'grey.300',
                                borderRadius: '3px',
                            },
                            scrollbarColor: `${palette.grey[300]} transparent`,
                        }}
                    >
                        <FlexyFiltersList filtersOpts={searchOpts?.filtersOpts} />
                    </Box>
                    {onRefresh && (
                        <Tooltip title="Rafraîchir les données">
                            <IconButton
                                onClick={onRefresh}
                                size="small"
                                sx={{ flexShrink: 0, marginBottom: '14px' }}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Grid>
        </Grid>
    );
};
